import React, { useState } from "react";
import { motion } from "framer-motion";
import { useUserUpdateBirthDateMutation } from "services/graphql";
import { useOnboardingAnalytics } from "@/analytics";
import { UserData } from "../../constants";
import styles from "../../styles.module.scss";

export interface BirthdayStepProps {
  onComplete: (data: Pick<UserData, "birthday">) => void;
  initialValue?: string;
}

export const BirthdayStep: React.FC<BirthdayStepProps> = ({
  onComplete,
  initialValue,
}) => {
  const [birthday, setBirthday] = useState(initialValue || "");
  const [error, setError] = useState("");
  const [updateBirthDate] = useUserUpdateBirthDateMutation();
  const {
    setBirthdayAnalytics,
    onboardingErrorAnalytics,
  } = useOnboardingAnalytics();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!birthday) {
      setError("Please enter your birthday");
      return;
    }

    // Basic validation - you might want to add more sophisticated validation
    const birthDate = new Date(birthday);
    const today = new Date();
    const minAge = 13; // Example minimum age requirement

    let currentAge = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      currentAge--;
    }

    if (currentAge < minAge) {
      const errorMessage = `You must be at least ${minAge} years old to use this platform`;
      setError(errorMessage);
      onboardingErrorAnalytics(errorMessage);
      return;
    }

    try {
      await updateBirthDate({
        variables: {
          input: {
            birthDate: birthday,
          },
        },
      });
      setBirthdayAnalytics(new Date(birthday));
      onComplete({ birthday });
    } catch (err) {
      const errorMessage = "Failed to update birth date. Please try again.";
      setError(errorMessage);
      onboardingErrorAnalytics(errorMessage);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      <h1 className={styles.title}>What&apos;s your date of birth?</h1>
      <p className={styles.subtitle}>
        You must be 13 years or older to use STEEZY Studio
      </p>

      <form onSubmit={handleSubmit}>
        <input
          type="date"
          value={birthday}
          onChange={e => {
            setBirthday(e.target.value);
            setError("");
          }}
          className={styles.input}
          max={new Date().toISOString().split("T")[0]}
        />

        {error && <p className={styles.error}>{error}</p>}

        <button type="submit" className={styles.button} disabled={!birthday}>
          CONTINUE
        </button>
      </form>
    </motion.div>
  );
};
